import React from "react";
import { Table } from "shared/core/Table";
import { CellProps, Column } from "react-table";
import { useCopies } from "shared/hooks/useCopies";
import { ManageParticipantTableProps } from "./types";
import ManageOrgAdminCell from "./components/manageOrgAdminCell";
import CellEllipsis from "shared/core/Table/components/cellEllipsis";
import ColumnHeaderWithTooltip from "shared/core/Table/components/columnHeaderWithTooltip";
import ManageMeetingRepCell from "./components/manageMeetingRepCell";
import { ApiProgramParticipantToManage } from "shared/types/ApiProgramParticipantToManage";

export default function ManageParticipantTable(props: ManageParticipantTableProps) {
  const copies = useCopies();

  const getDisplayName = React.useCallback(
    ({ firstName, lastName, loggedInUser }: ApiProgramParticipantToManage) =>
      loggedInUser
        ? copies.get("participant.name.me", {
            0: `${firstName} ${lastName}`
          })
        : `${firstName} ${lastName}`,
    [copies]
  );

  const tableColumns: Array<Column<ApiProgramParticipantToManage>> = React.useMemo(() => {
    return [
      {
        id: "name",
        Header: copies.get("name"),
        accessor: getDisplayName,
        width: 100,
        Cell: CellEllipsis
      },
      {
        id: "workEmail",
        Header: copies.get("work.email"),
        accessor: "workEmail",
        width: 130,
        Cell: CellEllipsis
      },
      {
        id: "managedByOrgAdmin",
        Header: (
          <ColumnHeaderWithTooltip
            className="manageOrgAdmin_headerColumn"
            headerText={copies.get("manage.as.org.admin")}
            tooltipText={
              props.isTradeProgram
                ? copies.get("mange.as.org.admin.tooltip.for.trade.program")
                : copies.get("mange.as.org.admin.tooltip")
            }
          />
        ),
        accessor: "managedByOrgAdmin",
        width: 90,
        Cell: (cellProps: CellProps<ApiProgramParticipantToManage>) => (
          <ManageOrgAdminCell
            {...cellProps}
            manageChangeHandler={props.manageChangeHandler}
            orgAdminProgramParticipantId={props.programParticipantId}
            programParticipantUiVersion={props.programParticipantsManagedByOrgAdmin?.uiVersion}
          />
        ),
        disableGlobalFilter: true,
        disableSortBy: true
      },
      {
        id: "meetingRepTagAllowed",
        Header: (
          <ColumnHeaderWithTooltip
            className="manageOrgAdmin_headerColumn"
            headerText={copies.get("hosted.meeting.rep")}
            tooltipText={
              props.isTradeProgram
                ? copies.get("hosted.meeting.rep.tooltip.for.trade.program")
                : copies.get("hosted.meeting.rep.tooltip")
            }
          />
        ),
        accessor: "meetingRepTagAllowed",
        width: 90,
        Cell: (cellProps: CellProps<ApiProgramParticipantToManage>) => (
          <ManageMeetingRepCell
            {...cellProps}
            programParticipantId={props.programParticipantId}
            programParticipantUiVersion={props.programParticipantsManagedByOrgAdmin?.uiVersion}
            manageChangeHandler={props.manageChangeHandler}
            programId={props.programParticipantsManagedByOrgAdmin?.programId}
            meetingCapInitialized={props.programParticipantsManagedByOrgAdmin?.meetingCapInitialized}
            meetingRepTagAddAllowedAsPerProgramState={
              props.programParticipantsManagedByOrgAdmin?.meetingRepTagAddAllowedAsPerProgramState
            }
            meetingRepTagRemoveAllowedAsPerProgramState={
              props.programParticipantsManagedByOrgAdmin?.meetingRepTagRemoveAllowedAsPerProgramState
            }
          />
        ),
        disableGlobalFilter: true,
        disableSortBy: true
      }
      // {/* intentionally commented; DO NOT REMOVE; */}
      // {/* todo:rr uncomment when we want to resend meeting rep ack email */}
      // {
      //   id: "meetingRepAckStatus",
      //   Header: copies.get("meeting.rep.ack.status.column"),
      //   accessor: "meetingRepAckStatus",
      //   width: 90,
      //   Cell: (cellProps: CellProps<ApiProgramParticipantToManage>) => (
      //     <ResendMeetingRepAckEmail
      //       meetingRepAckStatus={cellProps.row.original.meetingRepAckStatus}
      //       programParticipantId={cellProps.row.original.id}
      //       hasAuthority={true}
      //     />
      //   ),
      //   disableGlobalFilter: true,
      //   disableSortBy: true
      // }
    ];
  }, [copies, getDisplayName, props]);

  const initialTableState = React.useMemo(() => {
    const hiddenColumns = [];
    if (!props.programParticipantsManagedByOrgAdmin?.meetingCapInitialized) {
      hiddenColumns.push("meetingRepTagAllowed");
    }
    if (props.programParticipantsManagedByOrgAdmin?.hideManageParticipants) {
      hiddenColumns.push("managedByOrgAdmin");
    }
    // {/* intentionally commented; DO NOT REMOVE; */}
    // {/* todo:rr uncomment when we want to resend meeting rep ack email */}
    // if (!props.isTradeProgram || !props.programParticipantsManagedByOrgAdmin?.meetingCapInitialized) {
    //   hiddenColumns.push("meetingRepAckStatus");
    // }
    return {
      hiddenColumns
    };
  }, [
    props.programParticipantsManagedByOrgAdmin?.hideManageParticipants,
    props.programParticipantsManagedByOrgAdmin?.meetingCapInitialized
  ]);

  const getRowId = React.useCallback((data: ApiProgramParticipantToManage) => data.eventParticipantId, []);

  return (
    <Table<ApiProgramParticipantToManage>
      getRowId={getRowId}
      tableRef={props.tableRef}
      data={props.programParticipantsManagedByOrgAdmin?.participants || []}
      columns={tableColumns}
      disableFilters
      optionsHeight={1}
      initialTableState={initialTableState}
    />
  );
}
